/**
 * Dates, times and numbers in the language the reader chose — never the one their browser
 * happens to be installed in.
 *
 * This module exists because the two are not the same thing and the difference is invisible
 * until it bites: `new Intl.DateTimeFormat(undefined, …)` reads the *browser's* locale, so a
 * Slovak architect who set Kalqa to Slovak on a German-language Windows saw a Slovak screen
 * with German dates in it. Every formatted moment goes through here, and here reads the
 * language switch.
 *
 * **The chosen locale, not the resolved one.** A message can fall back to English per key
 * (`catalog.ts`); a date cannot and should not — somebody who picked Slovak wants a Slovak
 * date whether or not the sentence around it has been translated yet. Date and number
 * conventions are a property of the reader, not of a translation's progress.
 *
 * **The short date is numeric in `sk`, `cs` and `de`, and `medium` in `en`**
 * (`MCK-20260521-010`, owner 2026-09-15: *"DD.MM.YYYY default in SK/CZ/AT/DE; user can
 * override"*). The digits and their separators still come from `Intl` — `20.08.2026` or
 * `20. 08. 2026` depending on the engine's data, both of which are the reader's own
 * convention. Nothing here assembles a date string by hand. The override is
 * `DateFormatChoice`, a per-browser preference held in the locale store; it changes the shape
 * and never the language.
 *
 * The formatters are cached per (locale, shape): constructing an `Intl.DateTimeFormat` is
 * expensive and a thirty-row table would otherwise construct one per cell.
 */

import { useCallback, useMemo } from 'react';

import type { DateFormatChoice, Locale } from './locales';
import { relativeTime } from './relativeTime';
import { useLocaleStore } from './store';

/** How long a moment stays relative before it is shown as a plain date (chat1.md §6.7). */
const RELATIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** The languages whose automatic short date is day-month-year digits. Named once, here. */
export const NUMERIC_DATE_LOCALES: readonly Locale[] = ['sk', 'cs', 'de'];

/** What a `DateFormatChoice` resolves to once the language is known. */
export type DateShape = 'numeric' | 'medium';

/** `auto` follows `NUMERIC_DATE_LOCALES`; an explicit choice wins in every language. */
export function resolveDateShape(locale: Locale, choice: DateFormatChoice = 'auto'): DateShape {
  if (choice !== 'auto') return choice;
  return NUMERIC_DATE_LOCALES.includes(locale) ? 'numeric' : 'medium';
}

function dateOptions(shape: DateShape): Intl.DateTimeFormatOptions {
  return shape === 'numeric'
    ? { day: '2-digit', month: '2-digit', year: 'numeric' }
    : { dateStyle: 'medium' };
}

function dateTimeOptions(shape: DateShape): Intl.DateTimeFormatOptions {
  // `Intl` refuses `timeStyle` beside the individual `day`/`month`/`year` fields (a
  // `TypeError`, not a fallback), so the numeric shape spells out the fields that
  // `timeStyle: 'short'` stands for.
  return shape === 'numeric'
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { dateStyle: 'medium', timeStyle: 'short' };
}

const TIME: Intl.DateTimeFormatOptions = { timeStyle: 'short' };

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(locale: Locale, shape: string, options: Intl.DateTimeFormatOptions) {
  const cacheKey = `${locale} ${shape}`;
  const cached = dateFormatters.get(cacheKey);
  if (cached !== undefined) return cached;
  const created = new Intl.DateTimeFormat(locale, options);
  dateFormatters.set(cacheKey, created);
  return created;
}

const numberFormatters = new Map<Locale, Intl.NumberFormat>();

function numberFormatter(locale: Locale) {
  const cached = numberFormatters.get(locale);
  if (cached !== undefined) return cached;
  const created = new Intl.NumberFormat(locale);
  numberFormatters.set(locale, created);
  return created;
}

/**
 * A timestamp the browser cannot parse is a server-side problem, not something to crash a row
 * over. The raw value is at least honest about what arrived.
 */
function format(iso: string, locale: Locale, shape: string, options: Intl.DateTimeFormatOptions) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return dateFormatter(locale, shape, options).format(at);
}

/**
 * One short date in a shape the caller names, outside a hook — for a control that previews
 * what each `DateFormatChoice` would draw (`features/profile/ProfilePage`).
 */
export function formatDateAs(iso: string, locale: Locale, choice: DateFormatChoice): string {
  const shape = resolveDateShape(locale, choice);
  return format(iso, locale, `date ${shape}`, dateOptions(shape));
}

export interface Formats {
  /** The language everything here is formatted in — the reader's choice. */
  readonly locale: Locale;
  /** `20.08.2026` or `20 Aug 2026`. For a date somebody reads as a date: an expiry, a column. */
  readonly date: (iso: string) => string;
  /** `20.08.2026 14:05` or `20 Aug 2026, 14:05`. When the time of day is part of the point. */
  readonly dateTime: (iso: string) => string;
  /** `14:05`. Only where the day is already established by its surroundings. */
  readonly time: (iso: string) => string;
  /** `2 hours ago`, `yesterday`. Always relative, however old. */
  readonly relative: (iso: string) => string;
  /**
   * Relative inside a week, an absolute date beyond it (chat1.md §6.7). This is the default
   * for anything that happened — "3 days ago" is how a person thinks about last Tuesday, and
   * "8 months ago" is not how they think about a date in January.
   */
  readonly moment: (iso: string) => string;
  /**
   * `1 234,5` or `1,234.5` — a number for a person to read, with the decimal and grouping
   * separators `Intl` gives the reader's language (no explicit `decimal` option, ever).
   *
   * **Never for a file.** CSV export writes a dot on purpose (`backend/src/kalqa/documents/csv.py`;
   * `MCK-20260521-010`: *"dot in CSV export"*) because a spreadsheet import reads the file's
   * convention, not the reader's. Nothing that produces a file may call this.
   */
  readonly number: (value: number) => string;
}

export function useFormats(): Formats {
  const locale = useLocaleStore((state) => state.locale);
  const dateFormat = useLocaleStore((state) => state.dateFormat);
  return useFormatsIn(locale, dateFormat);
}

/**
 * The same formatters, in a language the caller names instead of the one the switch holds.
 *
 * The twin of `useMessagesIn`, and here for the same one reason: the share surface resolves
 * its own language from the browser and the sending project (`KLQ-20260826-002` §F), and an
 * issue date formatted out of the switch would be the German-dates-on-a-Slovak-screen defect
 * this module exists to prevent, one level up.
 *
 * **`choice` is optional and defaults to `auto`.** The share surface passes a locale alone: a
 * viewer's private date preference belongs to this browser and must not follow a link that
 * somebody else sent.
 */
export function useFormatsIn(locale: Locale, choice: DateFormatChoice = 'auto'): Formats {
  const shape = resolveDateShape(locale, choice);

  const date = useCallback(
    (iso: string) => format(iso, locale, `date ${shape}`, dateOptions(shape)),
    [locale, shape],
  );
  const dateTime = useCallback(
    (iso: string) => format(iso, locale, `dateTime ${shape}`, dateTimeOptions(shape)),
    [locale, shape],
  );
  const time = useCallback((iso: string) => format(iso, locale, 'time', TIME), [locale]);
  const relative = useCallback((iso: string) => relativeTime(iso, locale), [locale]);
  const number = useCallback((value: number) => numberFormatter(locale).format(value), [locale]);

  const moment = useCallback(
    (iso: string) => {
      const at = new Date(iso).getTime();
      if (Number.isNaN(at)) return iso;
      const age = Math.abs(Date.now() - at);
      if (age < RELATIVE_WINDOW_MS) return relativeTime(iso, locale);
      return date(iso);
    },
    [locale, date],
  );

  return useMemo(
    () => ({ locale, date, dateTime, time, relative, moment, number }),
    [locale, date, dateTime, time, relative, moment, number],
  );
}
